import React from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Clock, Calendar, Users, Award, CheckCircle2, ArrowRight } from 'lucide-react';

const CourseDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const courseId = location.pathname.split('/').pop();

  const courses = {
    drones: {
      title: 'Drone Technology',
      description: 'Master drone operations, maintenance, and applications in various industries.',
      duration: '12 weeks',
      startDate: 'April 15, 2025',
      batchSize: '20 students',
      price: '$1,499',
      image: 'https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&q=80&w=400',
      syllabus: [
        {
          week: 'Week 1-2',
          title: 'Fundamentals of Drone Technology',
          topics: [
            'Introduction to UAV systems',
            'Drone components and architecture',
            'Basic aerodynamics',
            'Flight principles'
          ]
        },
        {
          week: 'Week 3-4',
          title: 'Flight Operations',
          topics: [
            'Flight controls and navigation',
            'Weather considerations',
            'Emergency procedures',
            'Flight planning and mission setup'
          ]
        },
        {
          week: 'Week 5-6',
          title: 'Advanced Operations',
          topics: [
            'Advanced flight maneuvers',
            'Autonomous flight programming',
            'Payload management',
            'Data collection techniques'
          ]
        },
        {
          week: 'Week 7-8',
          title: 'Maintenance and Troubleshooting',
          topics: [
            'Routine maintenance procedures',
            'Component replacement',
            'Troubleshooting common issues',
            'Safety protocols'
          ]
        },
        {
          week: 'Week 9-10',
          title: 'Industry Applications',
          topics: [
            'Aerial photography and videography',
            'Mapping and surveying',
            'Agricultural applications',
            'Industrial inspections'
          ]
        },
        {
          week: 'Week 11-12',
          title: 'Regulations and Certification',
          topics: [
            'Drone regulations and compliance',
            'Licensing requirements',
            'Safety standards',
            'Industry certification preparation'
          ]
        }
      ]
    },
    embedded: {
      title: 'Embedded Systems',
      description: 'Learn microcontroller programming and embedded system design.',
      duration: '16 weeks',
      startDate: 'April 1, 2025',
      batchSize: '15 students',
      price: '$1,799',
      image: 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?auto=format&fit=crop&q=80&w=400',
      syllabus: [
        {
          week: 'Week 1-3',
          title: 'Introduction to Embedded Systems',
          topics: [
            'Microcontroller architecture',
            'Development environment setup',
            'Basic electronics',
            'Digital logic'
          ]
        },
        {
          week: 'Week 4-6',
          title: 'Programming Fundamentals',
          topics: [
            'C programming for embedded systems',
            'Memory management',
            'Interrupts and timers',
            'GPIO programming'
          ]
        },
        {
          week: 'Week 7-9',
          title: 'Communication Protocols',
          topics: [
            'UART, SPI, and I2C',
            'Network protocols',
            'Wireless communication',
            'Protocol debugging'
          ]
        },
        {
          week: 'Week 10-12',
          title: 'Real-time Operating Systems',
          topics: [
            'RTOS concepts',
            'Task management',
            'Inter-task communication',
            'Resource management'
          ]
        },
        {
          week: 'Week 13-14',
          title: 'Sensors and Actuators',
          topics: [
            'Sensor interfacing',
            'Signal conditioning',
            'Motor control',
            'Data acquisition'
          ]
        },
        {
          week: 'Week 15-16',
          title: 'Project Development',
          topics: [
            'System design',
            'Testing and debugging',
            'Performance optimization',
            'Documentation'
          ]
        }
      ]
    },
    frontend: {
      title: 'Frontend Development',
      description: 'Master modern web development technologies and frameworks.',
      duration: '14 weeks',
      startDate: 'March 20, 2025',
      batchSize: '25 students',
      price: '$1,299',
      image: 'https://images.unsplash.com/photo-1593720213428-28a5b9e94613?auto=format&fit=crop&q=80&w=400',
      syllabus: [
        {
          week: 'Week 1-2',
          title: 'Web Fundamentals',
          topics: [
            'HTML5 semantics',
            'CSS3 fundamentals',
            'Responsive design',
            'CSS frameworks'
          ]
        },
        {
          week: 'Week 3-4',
          title: 'JavaScript Essentials',
          topics: [
            'JavaScript fundamentals',
            'DOM manipulation',
            'Event handling',
            'Async programming'
          ]
        },
        {
          week: 'Week 5-6',
          title: 'React Fundamentals',
          topics: [
            'React components',
            'State management',
            'Props and lifecycle',
            'Hooks'
          ]
        },
        {
          week: 'Week 7-8',
          title: 'Advanced React',
          topics: [
            'Context API',
            'Redux',
            'Performance optimization',
            'Testing'
          ]
        },
        {
          week: 'Week 9-10',
          title: 'Modern Development Tools',
          topics: [
            'Git version control',
            'Package management',
            'Build tools',
            'Deployment'
          ]
        },
        {
          week: 'Week 11-14',
          title: 'Project Development',
          topics: [
            'Project architecture',
            'Best practices',
            'Performance optimization',
            'Real-world application'
          ]
        }
      ]
    },
    hacking: {
      title: 'Ethical Hacking',
      description: 'Learn cybersecurity fundamentals and ethical hacking techniques.',
      duration: '10 weeks',
      startDate: 'April 10, 2025',
      batchSize: '20 students',
      price: '$1,599',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=400',
      syllabus: [
        {
          week: 'Week 1-2',
          title: 'Introduction to Cybersecurity',
          topics: [
            'Security fundamentals',
            'Network basics',
            'Linux fundamentals',
            'Security tools'
          ]
        },
        {
          week: 'Week 3-4',
          title: 'Information Gathering',
          topics: [
            'Reconnaissance techniques',
            'OSINT',
            'Network scanning',
            'Vulnerability assessment'
          ]
        },
        {
          week: 'Week 5-6',
          title: 'System Hacking',
          topics: [
            'Password cracking',
            'Privilege escalation',
            'Malware analysis',
            'System hardening'
          ]
        },
        {
          week: 'Week 7-8',
          title: 'Web Application Security',
          topics: [
            'Web vulnerabilities',
            'SQL injection',
            'XSS attacks',
            'Security headers'
          ]
        },
        {
          week: 'Week 9-10',
          title: 'Advanced Topics',
          topics: [
            'Wireless security',
            'Mobile security',
            'Cloud security',
            'Incident response'
          ]
        }
      ]
    }
  };

  const course = courses[courseId as keyof typeof courses];

  if (!course) {
    navigate('/courses');
    return null;
  }

  const handleEnroll = () => {
    const message = `Hi, I'm interested in enrolling in the ${course.title} course starting ${course.startDate}`;
    const whatsappUrl = `https://wa.me/1234567890?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-xl overflow-hidden mb-12"
        >
          <div className="md:flex">
            <div className="md:w-1/2">
              <img
                src={course.image}
                alt={course.title}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="md:w-1/2 p-8">
              <h1 className="text-3xl font-bold mb-4">{course.title}</h1>
              <p className="text-gray-600 mb-6">{course.description}</p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-blue-600 mr-2" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                  <span>{course.startDate}</span>
                </div>
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-blue-600 mr-2" />
                  <span>{course.batchSize}</span>
                </div>
                <div className="flex items-center">
                  <Award className="h-5 w-5 text-blue-600 mr-2" />
                  <span>Certificate</span>
                </div>
              </div>

              <div className="mb-8">
                <div className="text-2xl font-bold text-blue-600 mb-2">
                  {course.price}
                </div>
                <p className="text-gray-600">
                  Includes course materials and certification
                </p>
              </div>

              <button
                onClick={handleEnroll}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-full hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                Enroll Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Syllabus Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-xl p-8"
        >
          <h2 className="text-2xl font-bold mb-8">Course Syllabus</h2>
          <div className="space-y-8">
            {course.syllabus.map((module, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="border-l-4 border-blue-600 pl-6"
              >
                <h3 className="text-xl font-semibold mb-2">{module.week}: {module.title}</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {module.topics.map((topic, i) => (
                    <li key={i} className="flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CourseDetails;